import './ErrorBoundary.css';
import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n';
import { logger } from '../utils/logger';

interface ErrorBoundaryProps {
    children?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Ловит исключения рендера поддерева и показывает заглушку вместо него.
 *
 * Без границы любая ошибка в листовом компоненте размонтирует ВСЁ дерево React:
 * #root остаётся пустым, экран чернеет, и приложение мертво до перезагрузки
 * страницы. Ровно так консоль падала на телеметрии с батареей неожиданного
 * формата — падение в BatteryBox уносило с собой карту, список и шапку.
 *
 * Границы бывают только классовыми компонентами: у хуков нет аналога
 * componentDidCatch.
 *
 * Подписи берём через i18n.t напрямую, а не через useTranslation: хуков в
 * классе нет. У каждой строки есть defaultValue — если namespace не успел
 * загрузиться, пользователь увидит текст, а не голый ключ.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        logger.error('Сбой в интерфейсе', error, info.componentStack);
    }

    private readonly retry = (): void => {
        this.setState({ hasError: false });
    };

    override render(): ReactNode {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div className='error-boundary' role='alert'>
                <div className='error-boundary-box'>
                    <h1 className='error-boundary-title'>
                        {i18n.t('error_title', { ns: 'console', defaultValue: 'Что-то пошло не так' })}
                    </h1>
                    <p className='error-boundary-text'>
                        {i18n.t('error_text', {
                            ns: 'console',
                            defaultValue: 'Этот экран не удалось отрисовать. Подробности — в консоли браузера.'
                        })}
                    </p>
                    <button type='button' className='error-boundary-retry' onClick={this.retry}>
                        {i18n.t('error_retry', { ns: 'console', defaultValue: 'Попробовать снова' })}
                    </button>
                </div>
            </div>
        );
    }
}
